import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { Select, Option } from 'src/components/PulseUI';
import { Button } from 'src/components';

interface TablePaginationProps {
  count: number;
  sortValues: {
    page: number;
    pageSize: number;
  };
  setSortValues: React.Dispatch<
    React.SetStateAction<{
      page: number;
      pageSize: number;
    }>
  >;
}

const TablePagination = ({
  count,
  sortValues,
  setSortValues,
}: TablePaginationProps) => {
  const totalPages = Math.ceil(count / sortValues.pageSize);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const maxVisiblePages = 5;

  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) return pageNumbers;

    const start = Math.max(
      1,
      sortValues.page - Math.floor(maxVisiblePages / 2)
    );
    const end = Math.min(totalPages, start + maxVisiblePages - 1);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <nav
      aria-label="Table pagination"
      data-testid="table-pagination"
      className="flex items-center justify-end gap-2"
    >
      <Select
        id="page-size-select"
        aria-label="Rows per page"
        variant="outlined"
        color="neutral"
        size="sm"
        className="w-20"
        value={sortValues.pageSize}
        onChange={(_, value) =>
          setSortValues({
            ...sortValues,
            pageSize: Number(value),
          })
        }
      >
        <Option value={50}>50</Option>
        <Option value={75}>75</Option>
        <Option value={100}>100</Option>
      </Select>

      <div className="flex items-center gap-1" role="group" aria-label="Page navigation">
        <Button
          variant="plain"
          color="neutral"
          className="p-1"
          onClick={() =>
            setSortValues({
              ...sortValues,
              page: Math.max(1, sortValues.page - 1),
            })
          }
          disabled={sortValues.page === 1}
          aria-label="Go to previous page"
          data-testid="pagination-prev"
        >
          <Icon icon={faChevronLeft} className="w-4 h-4" />
        </Button>

        {getVisiblePages().map((page) => (
          <Button
            key={page}
            variant="plain"
            className={`w-8 h-8 text-sm rounded-full transition-colors duration-150 ${
              page === sortValues.page
                ? 'bg-sky-600 text-white'
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            onClick={() =>
              setSortValues({
                ...sortValues,
                page,
              })
            }
            aria-label={`Go to page ${page}`}
            aria-current={page === sortValues.page ? 'page' : undefined}
            data-testid={`pagination-page-${page}`}
          >
            {page}
          </Button>
        ))}

        <Button
          variant="plain"
          color="neutral"
          className="p-1"
          onClick={() =>
            setSortValues({
              ...sortValues,
              page: Math.min(totalPages, sortValues.page + 1),
            })
          }
          disabled={sortValues.page === totalPages}
          aria-label="Go to next page"
          data-testid="pagination-next"
        >
          <Icon icon={faChevronRight} className="w-4 h-4" />
        </Button>
      </div>
    </nav>
  );
};

export default TablePagination;
